import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Send, ArrowLeft, Clock, Flame, Power, Share2, Plus, Users, Check, Compass, Wind, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

export default function Room() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isHost, setIsHost] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  
  // Real-time would ideally use Socket.io here, 
  // but for the scaffold we'll mock the fetch.
  
  const [travelersCount, setTravelersCount] = useState(0);
  const [showIdentityModal, setShowIdentityModal] = useState(false);
  const [tempNickname, setTempNickname] = useState("");
  const [tempAvatar, setTempAvatar] = useState("🕯️");

  const avatars = ["🕯️", "🌳", "🌊", "⛰️", "🔥", "✨", "🌙", "🦉", "🦊", "🐘"];

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  const fetchRoom = async () => {
    try {
      const storedNickname = localStorage.getItem('nickname');
      if (!storedNickname && !loading) {
        setShowIdentityModal(true);
        return;
      }

      const guestId = localStorage.getItem('guestId');
      const res = await api.get(`/sangha/rooms/${id}?guestId=${guestId || ''}`);
      setRoom(res.data.room);
      setMessages(res.data.room.messages || []);
      setTravelersCount(res.data.travelersCount || 1);
      if (!isEditingName) setEditedName(res.data.room.name || "");
      
      // Determine if current user/guest is host
      if (user && res.data.room.hostUserId === user.id) {
        setIsHost(true);
      } else if (!user && guestId && res.data.room.hostGuestId === guestId) {
        setIsHost(true);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 410) {
        toast.error("This room has dissolved.");
        navigate("/sangha");
      } else if (err.response?.status === 403) {
        toast.error(err.response.data.error || "Room is full.");
        navigate("/sangha");
      } else if (err.response?.status === 401) {
        setShowIdentityModal(true);
      } else {
        toast.error("Failed to load room.");
        navigate("/sangha");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateName = async () => {
    try {
        const guestId = localStorage.getItem('guestId');
        await api.post(`/sangha/rooms/${id}/rename`, { name: editedName, guestId });
        setIsEditingName(false);
        fetchRoom();
        toast.success("Circle renamed");
    } catch (e) {
        toast.error("Failed to rename circle");
    }
  };

  const handleLeave = async () => {
    const guestId = localStorage.getItem('guestId');
    try {
        await api.post(`/sangha/rooms/${id}/leave`, { guestId });
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchRoom();
    
    // Heartbeat for presence - every 20 seconds
    const presenceInterval = setInterval(fetchRoom, 20000);
    
    // Fast polling for messages - every 4 seconds
    const messageInterval = setInterval(async () => {
        try {
            const guestId = localStorage.getItem('guestId');
            const res = await api.get(`/sangha/rooms/${id}?guestId=${guestId || ''}`);
            setMessages(res.data.room.messages || []);
            setTravelersCount(res.data.travelersCount || 1);
        } catch (e) { /* ignore */ }
    }, 4000);
    
    // Explicit leave on unmount or navigation
    window.addEventListener('beforeunload', handleLeave);

    return () => {
        clearInterval(presenceInterval);
        clearInterval(messageInterval);
        window.removeEventListener('beforeunload', handleLeave);
        handleLeave();
    };
  }, [id, user, navigate]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    const guestId = localStorage.getItem('guestId');
    try {
      const res = await api.post(`/sangha/rooms/${id}/messages`, { 
        content: newMessage,
        guestId 
      });
      setMessages([...messages, res.data.message]);
      setNewMessage("");
      // Scroll to bottom
      setTimeout(() => {
        const chatContainer = document.getElementById('chat-container');
        if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
      }, 100);
    } catch (err) {
      toast.error("Failed to share thought.");
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Room link copied to clipboard!");
  };

  const handleExtendRoom = async () => {
    const guestId = localStorage.getItem('guestId');
    try {
      await api.post(`/sangha/rooms/${id}/extend`, { testExtend: true, guestId });
      toast.success("The fire has been tended. Life extended to 48h.");
      fetchRoom();
    } catch (err) {
      toast.error("Extension failed.");
    }
  };

  const handleEndRoom = async () => {
    if (!window.confirm("Are you sure you want to end this room prematurely? All activity will stop immediately.")) return;
    
    const guestId = localStorage.getItem('guestId');
    try {
      await api.delete(`/sangha/rooms/${id}?guestId=${guestId || ''}`);
      toast.success("Room ended.");
      navigate("/sangha");
    } catch (err) {
      toast.error("Failed to end room.");
    }
  };

  const isHinglish = language === 'hi';

  if (loading) {
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#05070a] text-white">
            <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 animate-pulse tracking-widest uppercase text-xs">Entering Sanctuary...</p>
        </div>
    );
  }

  if (!room) {
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#05070a] text-white p-6 text-center">
            <h2 className="text-2xl font-bold mb-2">The fire has gone out</h2>
            <p className="text-gray-500 mb-6">We couldn't find the circle you're looking for.</p>
            <button onClick={() => navigate("/sangha")} className="px-6 py-2 bg-indigo-600 rounded-full">Return to Lobby</button>
        </div>
    );
  }

  // Theme styling - Deep, atmospheric gradients
  const themeClasses = {
    forest: "bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-emerald-900 via-green-950 to-black",
    beach: "bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-sky-900 via-blue-950 to-black",
    mountains: "bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-slate-800 via-slate-950 to-black"
  };

  return (
    <div className={`fixed inset-0 z-50 flex flex-col transition-colors duration-1000 ${themeClasses[room.theme] || themeClasses.forest}`}>
      {/* Animated Atmospheric Layer */}
      <div className="absolute inset-0 opacity-30 pointer-events-none overflow-hidden">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-white/5 blur-[120px] rounded-full animate-pulse"></div>
        <div className="absolute top-[40%] -right-[10%] w-[30%] h-[50%] bg-white/5 blur-[100px] rounded-full animate-pulse duration-[5s]"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between p-4 md:p-6 bg-black/10 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-2 md:gap-4">
          <button 
            onClick={async () => {
                await handleLeave();
                navigate("/sangha");
            }}
            className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/80 transition-all active:scale-90"
            title="Leave Sangha"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <button 
            onClick={handleShare}
            className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/80 transition-all active:scale-90"
            title="Copy Invite Link"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex flex-col items-center flex-1 max-w-[50%]">
            {isEditingName ? (
                <div className="flex items-center gap-2 w-full max-w-xs">
                    <input 
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        autoFocus
                        className="bg-white/5 border border-white/20 rounded-lg px-3 py-1 text-white text-sm w-full focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                        onKeyPress={(e) => e.key === 'Enter' && handleUpdateName()}
                    />
                    <button onClick={handleUpdateName} className="text-emerald-400 hover:text-emerald-300">
                        <Check className="w-5 h-5" />
                    </button>
                </div>
            ) : (
                <h2 
                    className={`text-white font-medium tracking-widest uppercase text-[10px] md:text-xs opacity-60 mb-1 flex items-center gap-2 ${isHost ? "cursor-pointer hover:opacity-100" : ""}`}
                    onClick={() => isHost && setIsEditingName(true)}
                >
                    {room.name || `${room.theme} ${isHinglish ? "Sangha" : "Circle"}`}
                    {isHost && <Plus className="w-3 h-3 rotate-45 opacity-40" />}
                </h2>
            )}
            <div className="flex items-center gap-2 text-white/90 text-xs md:text-sm font-display">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                {travelersCount} Traveler{travelersCount !== 1 ? 's' : ''}
            </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden lg:flex items-center gap-2 text-white/80 text-sm bg-white/5 border border-white/10 px-4 py-2 rounded-full backdrop-blur-md">
            <Clock className="w-4 h-4 text-emerald-400" />
            <span>
              {Math.max(0, Math.floor((new Date(room.expiresAt) - new Date()) / (1000 * 60 * 60)))}h left
            </span>
          </div>
          
          <button 
            onClick={handleExtendRoom}
            className="flex items-center gap-2 text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 px-3 md:px-5 py-2 md:py-2.5 rounded-full text-[10px] md:text-sm font-semibold shadow-lg transition-all active:scale-95"
          >
            <Flame className="w-4 h-4" />
            <span className="hidden sm:inline">{isHinglish ? "Extend" : "Tend Fire"}</span>
          </button>

          {isHost && (
            <button 
              onClick={handleEndRoom}
              className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-red-400/80 bg-red-500/5 hover:bg-red-500/10 border border-red-500/10 rounded-full transition-all active:scale-90"
              title="End Room Now"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div id="chat-container" className="relative z-10 flex-1 overflow-y-auto p-4 md:p-12 flex flex-col scroll-smooth">
        <div className="max-w-3xl w-full mx-auto mt-auto space-y-6">
          
          <div className="text-center py-12 border-b border-white/5 mb-8">
            <div className="inline-block p-4 rounded-full bg-white/5 backdrop-blur-md mb-4">
                <Wind className="w-8 h-8 text-indigo-400 animate-pulse" />
            </div>
            <p className="text-white/30 text-xs font-medium tracking-[0.2em] uppercase">
              {isHinglish 
                ? "Yeh ek surakshit jagah hai. Aapki baatein yahan private hain." 
                : "This is a safe space. The fire burns for all who gather."}
            </p>
          </div>

          {messages.map((msg, idx) => {
             const isMe = (user && msg.senderUserId === user.id) || (!user && msg.senderGuestId === localStorage.getItem('guestId'));
             const senderNickname = msg.senderUser?.name || msg.senderGuest?.nickname || "Seeker";
             const senderAvatar = msg.senderUser ? "👤" : msg.senderGuest?.avatar || "🕯️";

             return (
                <div 
                  key={idx} 
                  className={`group flex flex-col ${isMe ? "items-end" : "items-start"} mb-2`}
                >
                    <div className={`flex items-end gap-2 ${isMe ? "flex-row-reverse" : "flex-row"}`}>
                        <div className={`w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm shadow-inner shrink-0 ${isMe ? "ml-1" : "mr-1"}`}>
                            {senderAvatar}
                        </div>
                        <div className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                            <span className="text-[10px] font-bold text-white/40 tracking-wider uppercase mb-1 ml-1 mr-1">
                                {senderNickname} {isMe && "(You)"}
                            </span>
                            <div className={`p-4 rounded-[1.5rem] text-white/90 leading-relaxed shadow-xl backdrop-blur-2xl transition-all ${
                                isMe 
                                ? "bg-white/10 border border-white/10 rounded-br-none" 
                                : "bg-black/40 border border-white/5 rounded-bl-none"
                            }`}>
                                <p className="text-sm md:text-[1.05rem] break-words max-w-[70vw] md:max-w-lg">{msg.content}</p>
                            </div>
                        </div>
                    </div>
                </div>
             );
          })}
        </div>
      </div>

      {/* Input Area */}
      <div className="relative z-10 p-4 md:p-6 pb-24 md:pb-10 bg-black/20 backdrop-blur-2xl border-t border-white/5">
        <div className="max-w-3xl mx-auto relative group">
          <input 
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder={isHinglish ? "Man ki baat..." : "Share a reflection..."}
            className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-[2rem] py-4 md:py-5 pl-6 md:pl-8 pr-14 md:pr-16 focus:outline-none focus:ring-2 focus:ring-white/20 placeholder-white/30 transition-all font-display text-sm md:text-lg"
          />
          <button 
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 disabled:opacity-20 disabled:scale-95 rounded-full text-white transition-all shadow-xl"
          >
            <Send className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </div>

      {/* Identity Modal for direct link entries */}
      {showIdentityModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/90 backdrop-blur-md">
            <div className="bg-[#0a0c10] border border-white/10 w-full max-w-md p-8 rounded-[2.5rem] shadow-2xl animate-in fade-in zoom-in duration-300">
                <h3 className="text-2xl font-bold text-white mb-2">Identify Yourself</h3>
                <p className="text-gray-500 text-sm mb-8">Before you enter this circle, who shall you be?</p>
                
                <div className="space-y-6">
                    <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Nickname</label>
                        <input 
                            type="text"
                            value={tempNickname}
                            onChange={(e) => setTempNickname(e.target.value)}
                            placeholder="Type a nickname..."
                            className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-6 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
                        />
                    </div>
                    
                    <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3 block">Avatar</label>
                        <div className="grid grid-cols-5 gap-3">
                            {avatars.map(a => (
                                <button 
                                    key={a}
                                    onClick={() => setTempAvatar(a)}
                                    className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all ${
                                        tempAvatar === a ? "bg-indigo-600 shadow-lg scale-110" : "bg-white/5 hover:bg-white/10"
                                    }`}
                                >
                                    <span className="text-xl">{a}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <button 
                        onClick={async () => {
                            if (!tempNickname.trim()) return toast.error("Nickname required");
                            localStorage.setItem('nickname', tempNickname);
                            localStorage.setItem('avatar', tempAvatar);
                            const guestId = localStorage.getItem('guestId');
                            try {
                                const res = await api.post('/sangha/guest', { guestId, nickname: tempNickname, avatar: tempAvatar });
                                if (res.data.guestId) localStorage.setItem('guestId', res.data.guestId);
                                setShowIdentityModal(false);
                                fetchRoom();
                            } catch (e) {
                                toast.error("Failed to join");
                            }
                        }}
                        className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl shadow-xl transition-all active:scale-95"
                    >
                        Enter Sanctuary
                    </button>
                    
                    <button 
                        onClick={() => navigate("/sangha")}
                        className="w-full py-2 text-gray-600 hover:text-gray-400 text-sm font-medium transition-all"
                    >
                        Back to Lobby
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}
